/**
 * Cynosure Titli storefront behaviours that apply across templates:
 * WhatsApp click tracking, device-local wishlist and recently-viewed product rails.
 * Product cards for the wishlist and recently-viewed rails are rendered server-side by the
 * `ct-product-cards` section through the Section Rendering API, so markup stays in Liquid.
 */

const WISHLIST_KEY = 'ct:wishlist';
const RECENT_KEY = 'ct:recently-viewed';
const RECENT_MAX = 12;

const storage = {
  /** @param {string} key */
  read(key) {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value : [];
    } catch {
      return [];
    }
  },
  /** @param {string} key @param {string[]} value */
  write(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* storage unavailable (private mode); wishlist simply won't persist */
    }
  },
};

/* ---------- WhatsApp click tracking ---------- */

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('a[href*="wa.me/"]') : null;
  if (!target) return;
  const context = target.getAttribute('data-ct-whatsapp') || 'link';
  try {
    window.Shopify?.analytics?.publish?.('whatsapp_click', { context, page: window.location.pathname });
  } catch {
    /* analytics is optional */
  }
});

/* ---------- Wishlist ---------- */

export const Wishlist = {
  all() {
    return storage.read(WISHLIST_KEY);
  },
  /** @param {string} id */
  has(id) {
    return this.all().includes(id);
  },
  /** @param {string} id */
  toggle(id) {
    const list = this.all();
    const next = list.includes(id) ? list.filter((h) => h !== id) : [id, ...list].slice(0, 60);
    storage.write(WISHLIST_KEY, next);
    document.dispatchEvent(new CustomEvent('ct:wishlist-change', { detail: { list: next } }));
    return next.includes(id);
  },
  /** @param {string} id */
  remove(id) {
    const next = this.all().filter((h) => h !== id);
    storage.write(WISHLIST_KEY, next);
    document.dispatchEvent(new CustomEvent('ct:wishlist-change', { detail: { list: next } }));
  },
};

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button || !this.dataset.productId) return;
    this.button.addEventListener('click', this.#onClick);
    document.addEventListener('ct:wishlist-change', this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
    document.removeEventListener('ct:wishlist-change', this.#sync);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    Wishlist.toggle(this.dataset.productId ?? '');
  };

  #sync = () => {
    const saved = Wishlist.has(this.dataset.productId ?? '');
    this.button?.setAttribute('aria-pressed', String(saved));
    const strings = window.ctConfig?.strings ?? {};
    const label = saved ? strings.wishlistRemove : strings.wishlistAdd;
    if (label) this.button?.setAttribute('aria-label', `${label}: ${this.dataset.title ?? ''}`.trim());
  };
}

class WishlistCount extends HTMLElement {
  connectedCallback() {
    document.addEventListener('ct:wishlist-change', this.#render);
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener('ct:wishlist-change', this.#render);
  }

  #render = () => {
    const count = Wishlist.all().length;
    this.textContent = count > 0 ? String(count) : '';
    this.hidden = count === 0;
  };
}

/* ---------- Product cards rendered via the Section Rendering API ---------- */

/**
 * Fetches server-rendered cards for a list of product IDs.
 * @param {string} query - Shopify search query, e.g. "id:1 OR id:2"
 * @param {string} sectionId - Section file used to render the result list
 */
async function fetchCards(query, sectionId) {
  const url = new URL(`${window.Shopify?.routes?.root || '/'}search`, window.location.origin);
  url.searchParams.set('q', query);
  url.searchParams.set('type', 'product');
  url.searchParams.set('options[prefix]', 'none');
  url.searchParams.set('section_id', sectionId);
  const response = await fetch(url.toString());
  if (!response.ok) throw new Error(`Card request failed: ${response.status}`);
  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.querySelector('[data-ct-cards]');
}

/**
 * Orders the fetched cards to match the stored order (search results come back by relevance).
 * @param {Element} container
 * @param {string[]} keys
 * @param {string} attribute
 */
function orderCards(container, keys, attribute) {
  const cards = Array.from(container.querySelectorAll(`[${attribute}]`));
  cards.sort((a, b) => keys.indexOf(a.getAttribute(attribute) ?? '') - keys.indexOf(b.getAttribute(attribute) ?? ''));
  cards.forEach((card) => container.append(card));
  return cards.length;
}

class WishlistGrid extends HTMLElement {
  connectedCallback() {
    document.addEventListener('ct:wishlist-change', this.#onChange);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('ct:wishlist-change', this.#onChange);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    if (!(event instanceof CustomEvent)) return;
    const list = /** @type {string[]} */ (event.detail.list);
    for (const card of this.querySelectorAll('[data-ct-id]')) {
      if (!list.includes(card.getAttribute('data-ct-id') ?? '')) card.remove();
    }
    this.#toggleEmpty(this.querySelectorAll('[data-ct-id]').length === 0);
  };

  /** @param {boolean} empty */
  #toggleEmpty(empty) {
    const emptyState = this.querySelector('[data-ct-empty]');
    const grid = this.querySelector('[data-ct-grid]');
    if (emptyState instanceof HTMLElement) emptyState.hidden = !empty;
    if (grid instanceof HTMLElement) grid.hidden = empty;
  }

  async render() {
    const ids = Wishlist.all();
    const grid = this.querySelector('[data-ct-grid]');
    if (!grid || ids.length === 0) {
      this.#toggleEmpty(true);
      this.removeAttribute('aria-busy');
      return;
    }
    try {
      const query = ids.map((h) => `id:${h}`).join(' OR ');
      const cards = await fetchCards(query, this.dataset.cardsSection || 'ct-product-cards');
      if (cards) {
        const count = orderCards(cards, ids, 'data-ct-id');
        grid.replaceChildren(...Array.from(cards.children));
        this.#toggleEmpty(count === 0);
      }
    } catch {
      this.#toggleEmpty(true);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

/* ---------- Recently viewed ---------- */

export const RecentlyViewedIds = {
  /** @param {string} id */
  add(id) {
    if (!id) return;
    const next = [id, ...storage.read(RECENT_KEY).filter((h) => h !== id)].slice(0, RECENT_MAX);
    storage.write(RECENT_KEY, next);
  },
  all() {
    return storage.read(RECENT_KEY);
  },
};

class RecentlyViewed extends HTMLElement {
  async connectedCallback() {
    const current = this.dataset.currentId ?? '';
    const ids = RecentlyViewedIds.all()
      .filter((h) => h !== current)
      .slice(0, Number(this.dataset.limit || 4));
    if (current) RecentlyViewedIds.add(current);
    if (ids.length === 0 || this.hasAttribute('data-design-mode')) return;

    try {
      const query = ids.map((h) => `id:${h}`).join(' OR ');
      const cards = await fetchCards(query, this.dataset.cardsSection || 'ct-product-cards');
      const grid = this.querySelector('[data-ct-grid]');
      if (!cards || !grid) return;
      const count = orderCards(cards, ids, 'data-ct-id');
      if (count === 0) return;
      grid.replaceChildren(...Array.from(cards.children));
      this.hidden = false;
    } catch {
      /* rail stays hidden */
    }
  }
}

if (!customElements.get('ct-wishlist-button')) customElements.define('ct-wishlist-button', WishlistButton);
if (!customElements.get('ct-wishlist-count')) customElements.define('ct-wishlist-count', WishlistCount);
if (!customElements.get('ct-wishlist-grid')) customElements.define('ct-wishlist-grid', WishlistGrid);
if (!customElements.get('ct-recently-viewed')) customElements.define('ct-recently-viewed', RecentlyViewed);

/* ---------- Google reviews (Trustindex) ---------- */

class GoogleReviews extends HTMLElement {
  connectedCallback() {
    const id = this.dataset.widgetId;
    if (!id || this.querySelector('script')) return;
    const load = () => {
      const script = document.createElement('script');
      script.src = `https://cdn.trustindex.io/loader.js?${encodeURIComponent(id)}`;
      script.defer = true;
      script.async = true;
      this.querySelector('[data-ct-slot]')?.append(script);
    };
    if (!('IntersectionObserver' in window)) return load();
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        load();
      },
      { rootMargin: '400px 0px' }
    );
    io.observe(this);
  }
}

if (!customElements.get('ct-google-reviews')) customElements.define('ct-google-reviews', GoogleReviews);
